
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useSupervisedData = (traineeUserId?: string) => {
  const [levelTestResults, setLevelTestResults] = useState<any[]>([]);
  const [puttingResults, setPuttingResults] = useState<any[]>([]);
  const [individualScores, setIndividualScores] = useState<any[]>([]);
  const [swingTendencies, setSwingTendencies] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (traineeUserId) {
      fetchSupervisedData();
    }
  }, [traineeUserId]);

  const fetchSupervisedData = async () => {
    if (!traineeUserId) return;

    try {
      setLoading(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Vérifier si le coach a les permissions
      const { data: coachData } = await supabase
        .from("coaches")
        .select("id")
        .eq("user_id", user.id)
        .single();

      if (!coachData) {
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Profil coach non trouvé",
        });
        return;
      }

      // Vérifier les permissions pour voir les résultats
      const { data: hasPermission } = await supabase.rpc(
        'has_data_permission',
        {
          _coach_user_id: user.id,
          _trainee_user_id: traineeUserId,
          _permission_type: 'view_results'
        }
      );

      if (!hasPermission) {
        toast({
          variant: "destructive",
          title: "Accès refusé",
          description: "Vous n'avez pas l'autorisation de voir ces données",
        });
        return;
      }

      // Récupérer les données des tests de niveau
      const { data: levelData, error: levelError } = await supabase
        .from("level_tests_results")
        .select("*")
        .eq("user_id", traineeUserId)
        .order("test_date", { ascending: false });

      if (levelError) throw levelError;

      // Récupérer les résultats de putting
      const { data: puttingData, error: puttingError } = await supabase
        .from("putting_test_results")
        .select("*")
        .eq("user_id", traineeUserId)
        .order("test_date", { ascending: false });

      if (puttingError) throw puttingError;

      // Récupérer les scores individuels
      const { data: scoresData, error: scoresError } = await supabase
        .from("individual_scores")
        .select("*")
        .eq("user_id", traineeUserId)
        .order("created_at", { ascending: false });

      if (scoresError) throw scoresError;

      // Récupérer les tendances de swing pour le stagiaire supervisé
      const { data: swingData, error: swingError } = await supabase
        .from("swing_tendencies")
        .select("*")
        .eq("user_id", traineeUserId)
        .order("test_date", { ascending: false });

      if (swingError) throw swingError;

      console.log("Swing tendencies fetched for trainee:", swingData?.length || 0);

      setLevelTestResults(levelData || []);
      setPuttingResults(puttingData || []);
      setIndividualScores(scoresData || []);
      setSwingTendencies(swingData || []);

    } catch (error: any) {
      console.error("Erreur lors de la récupération des données supervisées:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de charger les données supervisées",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    levelTestResults,
    puttingResults,
    individualScores,
    swingTendencies,
    loading,
    refetch: fetchSupervisedData
  };
};
